<template>
  <div ref="root" class="ex-menu" @keydown="onKeydown">
    <LgButton size="sm" variant="ghost" :aria-pressed="open" @click="toggleOpen">
      <template #icon><BookMarked :size="14" aria-hidden="true" /></template>
      Saved explorations
      <ChevronDown :size="13" aria-hidden="true" />
    </LgButton>

    <div v-if="open" class="panel" role="menu" aria-label="Saved explorations">
      <button
        type="button"
        class="save-current"
        :disabled="!canSave"
        :title="canSave ? '' : 'Pick a metric first'"
        @click="openSaveModal"
      >
        <Plus :size="14" aria-hidden="true" />
        Save current…
      </button>

      <div v-if="explorations.loading.value" class="panel-note">Loading…</div>
      <div v-else-if="explorations.error.value" class="panel-note err">
        Couldn't load saved explorations.
      </div>
      <div v-else-if="!ordered.length" class="panel-note">No saved explorations yet.</div>

      <ul v-else class="set-list">
        <li v-for="s in ordered" :key="s.id" class="set-item">
          <button
            type="button"
            class="star-btn"
            :class="{ on: isFavorite(s.id) }"
            :aria-pressed="isFavorite(s.id)"
            :aria-label="isFavorite(s.id) ? `Unfavorite ${s.name}` : `Favorite ${s.name}`"
            :title="isFavorite(s.id) ? 'Unfavorite' : 'Favorite'"
            @click.stop="onToggleFavorite(s.id)"
          >
            <Star :size="14" aria-hidden="true" />
          </button>

          <button type="button" class="set-name" @click="applyExploration(s)">{{ s.name }}</button>

          <span v-if="confirmDeleteId === s.id" class="confirm-inline">
            <span class="confirm-q">Delete?</span>
            <button type="button" class="confirm-yes" @click.stop="onConfirmDelete(s.id)">
              Delete
            </button>
            <button type="button" class="confirm-no" @click.stop="confirmDeleteId = null">
              Cancel
            </button>
          </span>
          <span v-else-if="confirmUpdateId === s.id" class="confirm-inline">
            <span class="confirm-q">Overwrite?</span>
            <button
              type="button"
              class="confirm-yes update"
              :disabled="updatingId === s.id"
              @click.stop="onConfirmUpdate(s)"
            >
              {{ updatingId === s.id ? 'Saving…' : 'Update' }}
            </button>
            <button
              type="button"
              class="confirm-no"
              @click.stop="
                confirmUpdateId = null;
                updateError = '';
              "
            >
              Cancel
            </button>
          </span>
          <span v-else class="set-actions">
            <button
              type="button"
              class="action-btn update-btn"
              :disabled="!canSave"
              :aria-label="`Update ${s.name} to the current exploration`"
              title="Update to current"
              @click.stop="startUpdate(s.id)"
            >
              <RefreshCw :size="14" aria-hidden="true" />
            </button>
            <button
              type="button"
              class="action-btn delete-btn"
              :aria-label="`Delete ${s.name}`"
              title="Delete"
              @click.stop="startDelete(s.id)"
            >
              <Trash2 :size="14" aria-hidden="true" />
            </button>
          </span>
        </li>
      </ul>

      <p v-if="updateError" class="panel-note err" role="alert">{{ updateError }}</p>
    </div>

    <LgModal :open="saveModalOpen" title="Save current exploration" @close="closeSaveModal">
      <div class="save-body">
        <LgInput
          v-model="saveName"
          placeholder="Exploration name"
          aria-label="Exploration name"
          @keydown.enter.prevent="onSave"
        />
        <p v-if="existingMatch" class="panel-note warn-note">
          An exploration named "{{ saveName.trim() }}" already exists — saving will overwrite it.
        </p>
        <p v-if="saveError" class="form-msg form-error" role="alert">{{ saveError }}</p>
      </div>
      <template #footer>
        <LgButton size="sm" variant="ghost" @click="closeSaveModal">Cancel</LgButton>
        <LgButton
          size="sm"
          variant="primary"
          :disabled="!saveName.trim() || saving"
          @click="onSave"
        >
          {{ saving ? 'Saving…' : existingMatch ? 'Overwrite' : 'Save' }}
        </LgButton>
      </template>
    </LgModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, watch } from 'vue';
import { BookMarked, ChevronDown, Star, RefreshCw, Trash2, Plus } from 'lucide-vue-next';
import { ApiError } from '../../api.js';
import {
  useExplorations,
  sortExplorations,
  type SavedExploration,
} from '../../composables/useExplorations.js';
import type { ExplorationSpec } from '../../lib/metric-exploration.js';
import LgButton from '../../ui/LgButton.vue';
import LgInput from '../../ui/LgInput.vue';
import LgModal from '../../ui/LgModal.vue';

const props = defineProps<{
  /** The current builder state serialized to a spec — what "Save current…"
   * persists. `null` when no metric is on the chart yet (Save is disabled). */
  spec: ExplorationSpec | null;
}>();

const emit = defineEmits<{ apply: [spec: ExplorationSpec] }>();

const canSave = computed(() => props.spec !== null);
const explorations = useExplorations();

const open = ref(false);
const confirmDeleteId = ref<number | null>(null);
const confirmUpdateId = ref<number | null>(null);
const updatingId = ref<number | null>(null);
const updateError = ref('');

const saveModalOpen = ref(false);
const saveName = ref('');
const saveError = ref('');
const saving = ref(false);

const ordered = computed(() =>
  sortExplorations(explorations.items.value, explorations.favorites.value),
);

function isFavorite(id: number): boolean {
  return explorations.favorites.value.includes(id);
}

function toggleOpen() {
  if (open.value) {
    close();
  } else {
    open.value = true;
    confirmDeleteId.value = null;
    confirmUpdateId.value = null;
    explorations.load();
  }
}

function close() {
  open.value = false;
  confirmDeleteId.value = null;
  confirmUpdateId.value = null;
}

function startDelete(id: number) {
  confirmUpdateId.value = null;
  updateError.value = '';
  confirmDeleteId.value = id;
}
function startUpdate(id: number) {
  if (!canSave.value) return;
  confirmDeleteId.value = null;
  updateError.value = '';
  confirmUpdateId.value = id;
}

function applyExploration(s: SavedExploration) {
  emit('apply', s.spec);
  close();
}

async function onToggleFavorite(id: number) {
  try {
    await explorations.toggleFavorite(id);
  } catch {
    /* optimistic update already rolled back inside useExplorations */
  }
}

async function onConfirmDelete(id: number) {
  try {
    await explorations.remove(id);
  } catch {
    /* optimistic update already rolled back inside useExplorations */
  }
  confirmDeleteId.value = null;
}

async function onConfirmUpdate(s: SavedExploration) {
  if (!props.spec) return;
  updateError.value = '';
  updatingId.value = s.id;
  try {
    await explorations.save(s.name, props.spec);
  } catch {
    updateError.value = 'Failed to update exploration.';
  }
  updatingId.value = null;
  confirmUpdateId.value = null;
}

const existingMatch = computed(() =>
  explorations.items.value.find((s) => s.name === saveName.value.trim()),
);

function openSaveModal() {
  if (!canSave.value) return;
  saveName.value = '';
  saveError.value = '';
  saving.value = false;
  saveModalOpen.value = true;
}

function closeSaveModal() {
  saveModalOpen.value = false;
}

async function onSave() {
  const name = saveName.value.trim();
  if (!name || !props.spec) return;
  saving.value = true;
  saveError.value = '';
  try {
    await explorations.save(name, props.spec);
    saveModalOpen.value = false;
    open.value = false;
  } catch (e) {
    saveError.value = e instanceof ApiError ? e.message : 'Failed to save exploration.';
  } finally {
    saving.value = false;
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && open.value && !saveModalOpen.value) {
    e.preventDefault();
    close();
  }
}

const root = ref<HTMLElement | null>(null);
function onDocPointer(e: MouseEvent) {
  if (open.value && root.value && !root.value.contains(e.target as Node)) close();
}
document.addEventListener('pointerdown', onDocPointer);
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocPointer));

watch(open, (isOpen) => {
  if (!isOpen) {
    confirmDeleteId.value = null;
    confirmUpdateId.value = null;
    updateError.value = '';
  }
});
</script>

<style scoped>
.ex-menu {
  position: relative;
  display: inline-flex;
}
.panel {
  position: absolute;
  z-index: var(--lg-z-menu);
  top: calc(100% + var(--lg-space-1));
  left: 0;
  width: 280px;
  max-width: calc(100vw - var(--lg-space-6));
  display: flex;
  flex-direction: column;
  padding: var(--lg-space-1);
  border-radius: var(--lg-radius-md);
  border: 1px solid var(--lg-border);
  background: var(--lg-bg-raised);
  box-shadow: var(--lg-shadow-2);
}
/* Mobile: viewport bottom-sheet (same pattern as FilterSetsMenu) so it never
   clips off the right edge at 390px. */
@media (max-width: 768px) {
  .panel {
    position: fixed;
    left: var(--lg-space-2);
    right: var(--lg-space-2);
    bottom: var(--lg-space-2);
    top: auto;
    width: auto;
    max-width: none;
    max-height: 75vh;
    overflow-y: auto;
  }
}
.save-current {
  display: flex;
  align-items: center;
  gap: var(--lg-space-2);
  width: 100%;
  min-height: var(--lg-control-md);
  padding: 0 var(--lg-space-3);
  border: none;
  border-radius: var(--lg-radius-sm);
  border-bottom: 1px solid var(--lg-border-faint);
  background: transparent;
  color: var(--lg-accent);
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}
.save-current:hover:not(:disabled) {
  background: var(--lg-bg-hover);
}
.save-current:disabled {
  opacity: 0.5;
  cursor: default;
}
.panel-note {
  padding: var(--lg-space-3);
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-sm);
}
.panel-note.err {
  color: var(--lg-danger);
}
.panel-note.warn-note {
  color: var(--lg-warn);
  padding: 0;
  margin: var(--lg-space-2) 0 0;
}
.set-list {
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 300px;
  overflow-y: auto;
}
.set-item {
  display: flex;
  align-items: center;
  gap: var(--lg-space-1);
}
.star-btn,
.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: var(--lg-control-sm);
  height: var(--lg-control-sm);
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text-faint);
  cursor: pointer;
}
.star-btn:hover,
.action-btn:hover:not(:disabled) {
  color: var(--lg-text);
  background: var(--lg-bg-hover);
}
.action-btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.star-btn.on {
  color: var(--lg-warn);
}
.set-actions {
  display: inline-flex;
  flex-shrink: 0;
  gap: 2px;
}
.delete-btn:hover {
  color: var(--lg-danger);
}
.set-name {
  flex: 1;
  min-width: 0;
  text-align: left;
  padding: var(--lg-space-2);
  border: none;
  border-radius: var(--lg-radius-sm);
  background: transparent;
  color: var(--lg-text);
  font: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}
.set-name:hover {
  background: var(--lg-bg-hover);
}
.confirm-inline {
  display: flex;
  align-items: center;
  gap: var(--lg-space-1);
  flex-shrink: 0;
  padding-right: var(--lg-space-1);
}
.confirm-q {
  color: var(--lg-text-faint);
  font-size: var(--lg-fs-xs);
}
.confirm-yes,
.confirm-no {
  border: none;
  border-radius: var(--lg-radius-sm);
  font: inherit;
  font-size: var(--lg-fs-xs);
  padding: var(--lg-space-1) var(--lg-space-2);
  cursor: pointer;
}
.confirm-yes {
  background: var(--lg-danger);
  color: var(--lg-bg);
}
.confirm-yes.update {
  background: var(--lg-accent);
}
.confirm-yes:disabled {
  opacity: 0.6;
  cursor: default;
}
.confirm-no {
  background: var(--lg-bg-surface);
  color: var(--lg-text);
  border: 1px solid var(--lg-border-strong);
}
.save-body {
  display: flex;
  flex-direction: column;
  gap: var(--lg-space-2);
}
.form-msg {
  margin: 0;
  font-size: var(--lg-fs-sm);
}
.form-error {
  color: var(--lg-danger);
}
</style>
